import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { browserHistory } from 'react-router';
import UrlPattern from 'url-pattern';

import { startDateISOSort } from '@evdy/web-core/dist/lib/utils';

import { EventsSection } from '../Events';
import MessageCTA from '../MessageCTA';
import NavBar from '../NavBar';
import CommunityCarousel from '../CommunityCarousel';
import DecedentInfo from '../DecedentInfo';
import AdSection from '../AdSection';
import Messages from '../Messages';
import ShareModal from '../ShareModal';
import SlideOutMenu from '../SlideOutMenu';
import CareTeamCTA from '../CareTeamCTA';
import FooterSection from '../FooterSection';
import MyEverdaysPanel from '../MyEverdaysPanel';

import { useFBShareRender } from './renderFBShareHook';

import { webCommunityOperations } from '@evdy/web-redux/dist/lib/webCommunity';
import { authUserOperations } from '@evdy/web-redux/dist/lib/authUser';
import { useHideIntercom, useScrollPosition } from '@evdy/web-core/dist/customHooks';

import styles from './WebCommunity.module.scss';

import postMessageIcon from '../../public/images/icons/post-message-icon-png.png';

const { trackAds, stageMessageValues } = webCommunityOperations;
const { loginById } = authUserOperations;

const {
  'community-page': communityPageClass,
  'decedent-bar-btn': decedentBarBtnClass,
  'help-section': helpSectionClass,
  'fixed-footer-cta': fixedFooterCTAClass,
  'sectional-border': sectionalBorderClass,
  'main-content': mainContentClass,
  'messages-section': messagesSectionClass,
  'my-everdays': myEverdaysClass
} = styles;

export const getBaseRoute = pathname => {
  const baseRoutes = [
    new UrlPattern('/funeral-homes/:country/:state/:city/:home/:memorialSlug*'),
    new UrlPattern('/announcements/:memorialSlug*'),
    new UrlPattern('/r/memorial/:memorialId*')
  ];

  for (let route of baseRoutes) {
    const match = route.match(pathname);
    if (match) {
      match._ = '';
      return route.stringify(match);
    }
  }
};

const WebCommunity = ({ location, children }) => {
  const {
    inviteMeta,
    decedentDetails,
    eventsArray = [],
    userName,
    userImage
  } = useSelector(({ webCommunity, authUser }) => ({
    inviteMeta: webCommunity.inviteMeta.data,
    decedentDetails: webCommunity.decedentDetails.data,
    eventsArray: webCommunity.events.array,
    userName: authUser?.data?.name,
    userImage: authUser?.data?.image
  }));
  const {
    _id: memorialId,
    ads = [],
    donations = [],
    obituary = '',
    shareUrl = ''
  } = inviteMeta || {};
  const { query } = location || {};
  const { userId: queryUserId } = query || {};
  const isEvents = !!eventsArray.length;

  const dispatch = useDispatch();

  const baseRoute = getBaseRoute(location.pathname);

  const postEvents = !eventsArray.length;
  const placementType = postEvents ? 'communityHomePostServices' : 'communityHome';
  const findAdsCb = ({ placementType: adDataPlacementType }) =>
    placementType === adDataPlacementType;
  const webCommunityAdData = !!ads.length && ads.find(findAdsCb);

  const [isShareModalOpen, setShareModalOpen] = useState(false);
  const [isMenuOpen, setMenuOpen] = useState(false);
  const [showDecedentBar, setShowDecedentBar] = useState(false);

  const sortedEvents = startDateISOSort([...eventsArray]);
  const handleEventClick = () => browserHistory.push(`${location.pathname}/event-details`);

  // get a throttled scroll height
  const scrollHeight = useScrollPosition();

  useHideIntercom('memorial', { showAfterClose: false });

  //render fb share when navigating between pages
  useFBShareRender();

  // log user in by id and set message staging for possible not you flow
  useEffect(() => {
    if (queryUserId) {
      dispatch(loginById(queryUserId));
      dispatch(stageMessageValues({ loginById: true }));
    }
  }, [dispatch, queryUserId]);

  // show/hide decedent bar based on scroll height
  useEffect(() => {
    const barRenderHeight = 80;
    if (scrollHeight >= barRenderHeight && !showDecedentBar) {
      setShowDecedentBar(true);
    } else if (scrollHeight < barRenderHeight && showDecedentBar) {
      setShowDecedentBar(false);
    }
  }, [scrollHeight, showDecedentBar]);

  const FixedFooterCTA = () => {
    const handlePostMessage = () => browserHistory.push(`${baseRoute}/condolence/create`);
    return (
      <div className={fixedFooterCTAClass}>
        <button onClick={handlePostMessage}>
          <img src={postMessageIcon} alt="post message" />
          <span>Post a message</span>
        </button>
      </div>
    );
  };

  const renderRoutedModal = () => {
    return React.Children.map(children, child => {
      const modalProps = {};

      return React.cloneElement(child, modalProps);
    });
  };

  const handleToggleShareMenu = () => {
    setShareModalOpen(prev => !prev);
  };

  const DecedentBarBtn = () => (
    <button className={decedentBarBtnClass} onClick={handleToggleShareMenu}>
      Share
    </button>
  );

  return (
    <div className={communityPageClass}>
      <NavBar ctaCopy="Resources" />
      <DecedentInfo
        openShareModal={handleToggleShareMenu}
        {...{ baseRoute, shareUrl }}
        {...decedentDetails}
      />
      <CommunityCarousel {...{ donations, obituary, eventsArray, baseRoute, shareUrl }} />
      {/* {isEvents && <EventsSection eventsArray={sortedEvents} onClick={handleEventClick} />} */}
      <div className={mainContentClass}>
        <div className={messagesSectionClass}>
          <MessageCTA {...{ baseRoute, userName, userImage, decedentDetails }} />
          <Messages {...{ baseRoute }} />
        </div>
        {/* <div className={myEverdaysClass}>
          <MyEverdaysPanel />
        </div> */}
      </div>
      <AdSection {...{ trackAds, memorialId }} adData={webCommunityAdData} />
      {/* <CareTeamCTA /> */}
      <FooterSection />
      <FixedFooterCTA />
      <ShareModal
        closeModal={handleToggleShareMenu}
        modalVisible={isShareModalOpen}
        shareUrl={inviteMeta.shareUrl}
      />
      <SlideOutMenu {...{ isMenuOpen, baseRoute }} closeMenu={() => setMenuOpen(false)} />
      {renderRoutedModal()}
    </div>
  );
};

export default WebCommunity;
